/**
 * Status Calculations Utility
 * Handles dynamic status calculations based on reservations and job schedules
 */

import dayjs from 'dayjs';

// Status constants
export const INVENTORY_STATUS = {
  AVAILABLE: 'available',
  RESERVED: 'reserved',
  UNAVAILABLE: 'unavailable'
};

export const STATUS_LABELS = {
  [INVENTORY_STATUS.AVAILABLE]: 'Available',
  [INVENTORY_STATUS.RESERVED]: 'Reserved',
  [INVENTORY_STATUS.UNAVAILABLE]: 'Unavailable'
};

export const STATUS_COLORS = {
  [INVENTORY_STATUS.AVAILABLE]: '#52c41a',    // Green
  [INVENTORY_STATUS.RESERVED]: '#faad14',     // Orange
  [INVENTORY_STATUS.UNAVAILABLE]: '#ff4d4f'  // Red
};

/**
 * Calculate status breakdown for a material type
 * @param {Object} inventory - Inventory quantities (new, used, damaged)
 * @param {Array} reservations - Active reservations
 * @param {Array} jobSchedules - Active job schedules
 * @param {Date} currentDate - Current date for calculations
 * @returns {Object} Status breakdown
 */
export const calculateStatusBreakdown = (inventory, reservations = [], jobSchedules = [], currentDate = new Date()) => {
  // Calculate usable quantity (new + used, excluding damaged)
  const usableQuantity = (inventory.newQuantity || 0) + (inventory.usedQuantity || 0);
  
  if (usableQuantity === 0) {
    return {
      availableQuantity: 0,
      reservedQuantity: 0,
      unavailableQuantity: 0,
      totalUsableQuantity: 0,
      damagedQuantity: inventory.damagedQuantity || 0,
      statusPercentages: {
        available: 0,
        reserved: 0,
        unavailable: 0
      }
    };
  }
  
  // Get quantities by date ranges
  const dateRanges = getDateRangesFromReservationsAndJobs(reservations, jobSchedules);
  const statusQuantities = categorizeQuantitiesByDateRange(usableQuantity, dateRanges, currentDate);
  
  // Calculate percentages
  const statusPercentages = {
    available: usableQuantity > 0 ? Math.round((statusQuantities.available / usableQuantity) * 100) : 0,
    reserved: usableQuantity > 0 ? Math.round((statusQuantities.reserved / usableQuantity) * 100) : 0,
    unavailable: usableQuantity > 0 ? Math.round((statusQuantities.unavailable / usableQuantity) * 100) : 0
  };
  
  return {
    availableQuantity: statusQuantities.available,
    reservedQuantity: statusQuantities.reserved,
    unavailableQuantity: statusQuantities.unavailable,
    totalUsableQuantity: usableQuantity,
    damagedQuantity: inventory.damagedQuantity || 0,
    statusPercentages
  };
};

/**
 * Extract date ranges from reservations and job schedules
 * @param {Array} reservations - Reservation records
 * @param {Array} jobSchedules - Job schedule records
 * @returns {Array} Array of date range objects
 */
export const getDateRangesFromReservationsAndJobs = (reservations, jobSchedules) => {
  const dateRanges = [];
  
  // Process reservations
  reservations.forEach(reservation => {
    if (reservation.startDate && reservation.endDate && reservation.status === 'active') {
      dateRanges.push({
        startDate: dayjs(reservation.startDate),
        endDate: dayjs(reservation.endDate),
        quantity: reservation.quantity || 0,
        type: 'reservation',
        id: reservation.id,
        source: reservation
      });
    }
  });
  
  // Process job schedules
  jobSchedules.forEach(job => {
    if (job.startDate && job.endDate && job.status === 'active') {
      dateRanges.push({
        startDate: dayjs(job.startDate),
        endDate: dayjs(job.endDate),
        quantity: job.quantity || 0,
        type: 'job',
        id: job.id,
        source: job
      });
    }
  });
  
  return dateRanges;
};

/**
 * Categorize quantities by date ranges relative to current date
 * @param {number} totalUsableQuantity - Total usable quantity
 * @param {Array} dateRanges - Array of date ranges
 * @param {Date} currentDate - Current date
 * @returns {Object} Categorized quantities
 */
export const categorizeQuantitiesByDateRange = (totalUsableQuantity, dateRanges, currentDate) => {
  const current = dayjs(currentDate);
  let reservedQuantity = 0;
  let unavailableQuantity = 0;
  
  dateRanges.forEach(range => {
    const startDate = dayjs(range.startDate);
    const endDate = dayjs(range.endDate);
    const quantity = range.quantity || 0;
    
    // Check if date range is in the future (reserved)
    if (startDate.isAfter(current) && endDate.isAfter(current)) {
      reservedQuantity += quantity;
    }
    // Check if date range is ongoing (unavailable)
    else if (startDate.isBefore(current) && endDate.isAfter(current)) {
      unavailableQuantity += quantity;
    }
    // Past date ranges don't affect current status
  });
  
  // Ensure we don't exceed total usable quantity
  const totalAllocated = reservedQuantity + unavailableQuantity;
  if (totalAllocated > totalUsableQuantity) {
    // Proportionally reduce if over-allocated
    const ratio = totalUsableQuantity / totalAllocated;
    reservedQuantity = Math.floor(reservedQuantity * ratio);
    unavailableQuantity = Math.floor(unavailableQuantity * ratio);
  }
  
  const availableQuantity = Math.max(0, totalUsableQuantity - reservedQuantity - unavailableQuantity);
  
  return {
    available: availableQuantity,
    reserved: reservedQuantity,
    unavailable: unavailableQuantity
  };
};

/**
 * Get active job quantities for a material type
 * @param {string} materialType - Material type
 * @param {Array} jobSchedules - Job schedule records
 * @param {Date} currentDate - Current date
 * @returns {number} Quantity currently in active jobs
 */
export const getActiveJobQuantities = (materialType, jobSchedules, currentDate = new Date()) => {
  const current = dayjs(currentDate);
  let activeQuantity = 0;
  
  jobSchedules.forEach(job => {
    if (job.materialType === materialType && job.status === 'active') {
      const startDate = dayjs(job.startDate);
      const endDate = dayjs(job.endDate);
      
      // Check if job is currently ongoing
      if (startDate.isBefore(current) && endDate.isAfter(current)) {
        activeQuantity += job.quantity || 0;
      }
    }
  });
  
  return activeQuantity;
};

/**
 * Get reserved quantities for a material type in a date range
 * @param {string} materialType - Material type
 * @param {Array} reservations - Reservation records
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {number} Quantity reserved in the date range
 */
export const getReservedQuantities = (materialType, reservations, startDate, endDate) => {
  const queryStart = dayjs(startDate);
  const queryEnd = dayjs(endDate);
  let reservedQuantity = 0;
  
  reservations.forEach(reservation => {
    if (reservation.materialType === materialType && reservation.status === 'active') {
      const resStart = dayjs(reservation.startDate);
      const resEnd = dayjs(reservation.endDate);
      
      // Check if reservation overlaps with query date range
      if (resStart.isBefore(queryEnd) && resEnd.isAfter(queryStart)) {
        reservedQuantity += reservation.quantity || 0;
      }
    }
  });
  
  return reservedQuantity;
};

/**
 * Calculate availability for a specific date range
 * @param {Object} inventory - Inventory quantities
 * @param {Array} reservations - Reservation records
 * @param {Array} jobSchedules - Job schedule records
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Object} Availability analysis
 */
export const calculateAvailabilityForDateRange = (inventory, reservations, jobSchedules, startDate, endDate) => {
  const usableQuantity = (inventory.newQuantity || 0) + (inventory.usedQuantity || 0);
  const queryStart = dayjs(startDate);
  const queryEnd = dayjs(endDate);
  
  let conflictingQuantity = 0;
  const conflicts = [];
  
  // Check reservations for conflicts
  reservations.forEach(reservation => {
    if (reservation.status === 'active') {
      const resStart = dayjs(reservation.startDate);
      const resEnd = dayjs(reservation.endDate);
      
      // Check for overlap
      if (resStart.isBefore(queryEnd) && resEnd.isAfter(queryStart)) {
        conflictingQuantity += reservation.quantity || 0;
        conflicts.push({
          type: 'reservation',
          id: reservation.id,
          startDate: reservation.startDate,
          endDate: reservation.endDate,
          quantity: reservation.quantity,
          source: reservation
        });
      }
    }
  });
  
  // Check job schedules for conflicts
  jobSchedules.forEach(job => {
    if (job.status === 'active') {
      const jobStart = dayjs(job.startDate);
      const jobEnd = dayjs(job.endDate);
      
      // Check for overlap
      if (jobStart.isBefore(queryEnd) && jobEnd.isAfter(queryStart)) {
        conflictingQuantity += job.quantity || 0;
        conflicts.push({
          type: 'job',
          id: job.id,
          startDate: job.startDate,
          endDate: job.endDate,
          quantity: job.quantity,
          source: job
        });
      }
    }
  });
  
  const availableQuantity = Math.max(0, usableQuantity - conflictingQuantity);
  
  return {
    totalUsableQuantity: usableQuantity,
    conflictingQuantity,
    availableQuantity,
    isAvailable: availableQuantity > 0,
    conflicts,
    dateRange: { startDate, endDate }
  };
};

/**
 * Get status priority for sorting
 * @param {string} status - Status value
 * @returns {number} Priority number (lower = higher priority)
 */
export const getStatusPriority = (status) => {
  const priorities = {
    [INVENTORY_STATUS.UNAVAILABLE]: 1,
    [INVENTORY_STATUS.RESERVED]: 2,
    [INVENTORY_STATUS.AVAILABLE]: 3
  };
  
  return priorities[status] || 999;
};

/**
 * Format status for display
 * @param {string} status - Status value
 * @returns {Object} Display formatting
 */
export const formatStatusDisplay = (status) => {
  return {
    label: STATUS_LABELS[status] || status,
    color: STATUS_COLORS[status] || '#666666',
    value: status,
    priority: getStatusPriority(status)
  };
};

/**
 * Get status summary for multiple materials
 * @param {Array} inventoryItems - Array of inventory items with status
 * @returns {Object} Summary statistics
 */
export const getStatusSummary = (inventoryItems) => {
  const summary = {
    totalMaterials: inventoryItems.length,
    totalUsableQuantity: 0,
    byStatus: {
      [INVENTORY_STATUS.AVAILABLE]: { count: 0, quantity: 0 },
      [INVENTORY_STATUS.RESERVED]: { count: 0, quantity: 0 },
      [INVENTORY_STATUS.UNAVAILABLE]: { count: 0, quantity: 0 }
    },
    statusPercentages: {
      available: 0,
      reserved: 0,
      unavailable: 0
    }
  };
  
  inventoryItems.forEach(item => {
    const statusBreakdown = item.statusBreakdown || {};
    
    summary.totalUsableQuantity += statusBreakdown.totalUsableQuantity || 0;
    
    // Count by status
    Object.values(INVENTORY_STATUS).forEach(status => {
      const quantity = statusBreakdown[`${status}Quantity`] || 0;
      if (quantity > 0) {
        summary.byStatus[status].count += 1;
        summary.byStatus[status].quantity += quantity;
      }
    });
  });
  
  // Calculate percentages
  if (summary.totalUsableQuantity > 0) {
    Object.keys(summary.statusPercentages).forEach(status => {
      const quantity = summary.byStatus[status]?.quantity || 0;
      summary.statusPercentages[status] = Math.round((quantity / summary.totalUsableQuantity) * 100);
    });
  }
  
  return summary;
};

/**
 * Validate status calculation inputs
 * @param {Object} inventory - Inventory data
 * @param {Array} reservations - Reservations array
 * @param {Array} jobSchedules - Job schedules array
 * @returns {Object} Validation result
 */
export const validateStatusInputs = (inventory, reservations, jobSchedules) => {
  const errors = [];
  
  if (!inventory || typeof inventory !== 'object') {
    errors.push('Inventory data is required and must be an object');
  }
  
  if (reservations && !Array.isArray(reservations)) {
    errors.push('Reservations must be an array');
  }
  
  if (jobSchedules && !Array.isArray(jobSchedules)) {
    errors.push('Job schedules must be an array');
  }
  
  // Validate inventory quantities
  if (inventory) {
    const quantities = ['newQuantity', 'usedQuantity', 'damagedQuantity'];
    quantities.forEach(qty => {
      if (inventory[qty] !== undefined && (typeof inventory[qty] !== 'number' || inventory[qty] < 0)) {
        errors.push(`${qty} must be a non-negative number`);
      }
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get all possible status values
 * @returns {Array} Array of status values
 */
export const getAllStatuses = () => {
  return Object.values(INVENTORY_STATUS);
};

/**
 * Check if a status is valid
 * @param {string} status - Status to validate
 * @returns {boolean} True if valid
 */
export const isValidStatus = (status) => {
  return Object.values(INVENTORY_STATUS).includes(status);
};
